import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import API, { setToken, clearToken, getToken } from '../lib/api';

const AuthContext = createContext(null);

function formatErr(err) {
  // Helper: onderscheid tussen netwerk-fout, timeout en server-fout
  if (!err) return 'Er ging iets mis.';
  // 1) Axios-error met response → toon het `detail`-veld uit de backend
  if (err.response) {
    const detail = err.response.data?.detail;
    const status = err.response.status;
    if (detail) {
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
      if (detail?.msg) return detail.msg;
      return String(detail);
    }
    // Geen detail — toon status + statusText
    return `Server-fout (${status}). Probeer opnieuw.`;
  }
  // 2) Request werd verstuurd maar geen respons ontvangen (timeout, CORS, netwerk down)
  if (err.request) {
    if (err.code === 'ECONNABORTED') return 'Time-out — server reageerde niet tijdig. Controleer je internetverbinding.';
    if (err.message?.includes('Network')) return 'Geen verbinding met de server. Check je internet of probeer opnieuw.';
    return 'Geen respons van de server. Controleer je verbinding en probeer opnieuw.';
  }
  // 3) Fout bij opstellen request
  return err.message || 'Er ging iets mis bij het verzenden van het verzoek.';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(null); // server traag/onbereikbaar bij opstarten
  const [bootPoging, setBootPoging] = useState(0);

  useEffect(() => {
    let actief = true;
    setBootError(null);
    // Sessie herstellen met harde time-out: bij een trage/herstartende server blijven we niet eindeloos op "Laden..." hangen
    API.get('/auth/me', { timeout: 10000, noCache: true })
      .then(r => { if (!actief) return; setUser(r.data); setLoading(false); })
      .catch((e) => {
        if (!actief) return;
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          // Echt niet (meer) ingelogd → token weg, naar login
          clearToken(); setUser(false); setLoading(false);
        } else if (e?.response) {
          // Server antwoordde met een andere fout (bv. 500/502): toon login maar bewaar token niet
          clearToken(); setUser(false); setLoading(false);
        } else {
          // Geen antwoord (time-out/netwerk): token BEHOUDEN en automatisch opnieuw proberen
          setBootError(formatErr(e));
          setTimeout(() => { if (actief) setBootPoging(n => n + 1); }, 4000);
        }
      });
    return () => { actief = false; };
  }, [bootPoging]);

  // Sessie definitief verlopen (refresh-token weg/ongeldig) → naar inlogscherm i.p.v. blijven hangen
  useEffect(() => {
    const h = () => {
      setUser(false); setLoading(false); setBootError(null);
      toast.info('Uw sessie is verlopen — log opnieuw in.', { id: 'sessie-verlopen' });
    };
    window.addEventListener('billfac:sessie-verlopen', h);
    return () => window.removeEventListener('billfac:sessie-verlopen', h);
  }, []);

  const login = useCallback(async (email, password) => {
    const doAttempt = async () => {
      const { data } = await API.post('/auth/login', { email, password }, { timeout: 20000 });
      // Token opslaan → Bearer-header voor alle toekomstige requests.
      // Dit omzeilt het probleem dat de Kubernetes-ingress CORS overschrijft met wildcard
      // waardoor Chrome cookies weigert. Refresh-token erbij → sessie wordt stil vernieuwd (30 d).
      if (data?.access_token) setToken(data.access_token, data.refresh_token || '');
      return data;
    };
    try {
      const data = await doAttempt();
      setUser(data);
      return { ok: true };
    } catch (e) {
      // Eerste poging faalde — als het een netwerk-fout is (geen response) proberen we
      // nog 1x na een korte delay. Dit helpt bij transiënte Cloudflare/SW-hiccups.
      if (!e.response && (e.request || e.code === 'ECONNABORTED' || (e.message || '').includes('Network'))) {
        try {
          await new Promise(r => setTimeout(r, 800));
          const data = await doAttempt();
          setUser(data);
          return { ok: true };
        } catch (e2) {
          // Log voor diagnose in console
          try { console.warn('[login] Herhaald mislukt:', e2?.code, e2?.message); } catch { /* stil */ }
          return { ok: false, error: formatErr(e2) };
        }
      }
      return { ok: false, error: formatErr(e) };
    }
  }, []);

  const register = useCallback(async (email, password, name, bedrijfsnaam, extra = {}) => {
    try {
      const payload = { email, password, name, bedrijfsnaam, ...extra };
      const { data } = await API.post('/auth/register', payload, { timeout: 30000 });
      if (data?.access_token) setToken(data.access_token, data.refresh_token || '');
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await API.post('/auth/logout'); } catch { /* stil */ }
    clearToken();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, bootError, bootPoging, retryBoot: () => setBootPoging(n => n + 1), naarLogin: () => { setBootError(null); setUser(false); setLoading(false); }, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
